import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookOpen, ArrowRight, CheckCircle, Target, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import Chart from '../components/Chart';

const Guides = () => {
  const guideLinks = [
    {
      id: 1,
      title: 'Comment Choisir une Plateforme de Paris en Ligne',
      slug: 'choisir-plateforme-paris',
      description: 'Découvrez les critères essentiels pour sélectionner la plateforme qui correspond à vos besoins et à votre style de jeu.',
    },
    {
      id: 2,
      title: 'Comprendre les Bonus de Bienvenue',
      slug: 'comprendre-bonus-bienvenue',
      description: 'Tout ce que vous devez savoir sur les offres promotionnelles : conditions, avantages et pièges à éviter.',
    },
    {
      id: 3,
      title: 'Les Types de Paris Sportifs',
      slug: 'types-paris-sportifs',
      description: 'Guide complet des différents types de paris disponibles : simples, combinés, live et bien plus encore.',
    },
    {
      id: 4,
      title: 'Guide des Paris Hippiques',
      slug: 'guide-paris-hippiques',
      description: 'Maîtrisez l\'art des paris sur les courses hippiques avec nos conseils d\'experts et stratégies éprouvées.',
    },
    {
      id: 5,
      title: 'Gestion de Bankroll',
      slug: 'gestion-bankroll',
      description: 'Apprenez à gérer votre budget de paris efficacement pour maximiser vos chances de succès sur le long terme.',
    },
    {
      id: 6,
      title: 'Stratégies de Paris',
      slug: 'strategies-paris',
      description: 'Découvrez les méthodes et techniques utilisées par les parieurs expérimentés pour améliorer leurs résultats.',
    },
    {
      id: 7,
      title: 'Sécurité et Protection des Données',
      slug: 'securite-protection-donnees',
      description: 'Comment protéger vos informations personnelles et financières lors de vos activités de paris en ligne.',
    },
    {
      id: 8,
      title: 'Retraits et Dépôts',
      slug: 'retraits-depots',
      description: 'Tout savoir sur les méthodes de paiement, les délais et les conditions pour déposer et retirer vos fonds.',
    },
    {
      id: 9,
      title: 'Jeu Responsable',
      slug: 'jeu-responsable',
      description: 'Les bonnes pratiques pour parier de manière responsable et maintenir un équilibre sain avec cette activité.',
    },
  ];

  const chart1Data = [
    { label: 'Sécurité', value: 95 },
    { label: 'Bonus', value: 88 },
    { label: 'Variété', value: 92 },
    { label: 'Support', value: 85 },
  ];

  const chart2Data = [
    { label: 'Football', value: 45 },
    { label: 'Tennis', value: 25 },
    { label: 'Hippiques', value: 20 },
    { label: 'Autres', value: 10 },
  ];

  const chart3Data = [
    { label: '2021', value: 65 },
    { label: '2022', value: 72 },
    { label: '2023', value: 78 },
    { label: '2024', value: 85 },
    { label: '2025', value: 92 },
  ];

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      <main className="py-20">
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <BookOpen className="w-16 h-16 text-toxic-green" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Guides : Comment Parier en Ligne en <span className="text-toxic-green">France</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Votre ressource complète pour maîtriser l'art des paris en ligne. Des bases aux stratégies avancées, tout ce dont vous avez besoin pour réussir.
            </p>
          </div>

          {/* About Our Guides Section */}
          <div className="bg-dark-card border-2 border-gray-800 rounded-2xl p-8 md:p-12 mb-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 border-2 border-toxic-green opacity-10 rotate-45 transform translate-x-20 -translate-y-20"></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Comment Nous Rédigeons Nos Guides
              </h2>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p className="text-lg">
                  Chez Hexabet Connect, nous ne nous contentons pas de compiler des informations. Chaque guide que nous créons est le fruit d'une analyse approfondie du marché français des paris en ligne, testé et vérifié par notre équipe d'experts.
                </p>
                <p>
                  Notre approche est simple mais rigoureuse : nous testons personnellement chaque plateforme, analysons les conditions générales, comparons les offres promotionnelles et évaluons l'expérience utilisateur réelle. Nous ne nous basons jamais sur des informations de seconde main ou des promesses marketing. Chaque donnée, chaque conseil, chaque recommandation que vous trouverez dans nos guides provient d'une expérience concrète et vérifiable.
                </p>
                <p>
                  Nous comprenons que le monde des paris en ligne peut sembler complexe, surtout pour les débutants. C'est pourquoi nous nous efforçons de rendre nos guides accessibles tout en restant complets. Nous expliquons les concepts techniques en termes simples, décomposons les stratégies complexes en étapes faciles à suivre, et fournissons toujours des exemples concrets pour illustrer nos points.
                </p>
                <p>
                  L'objectivité est au cœur de notre démarche. Bien que nous puissions recevoir des commissions via des liens d'affiliation, cela n'influence jamais notre analyse. Si une plateforme présente des faiblesses, nous les mentionnons. Si une offre semble trop belle pour être vraie, nous vous expliquons pourquoi. Notre réputation dépend de votre confiance, et nous la protégeons en étant toujours transparents et honnêtes.
                </p>
                <p>
                  Nous mettons également à jour régulièrement nos guides pour refléter les changements du marché. Les conditions de bonus évoluent, de nouvelles plateformes apparaissent, les réglementations changent. Notre engagement est de maintenir nos guides à jour avec les informations les plus récentes et les plus précises disponibles.
                </p>
                <div className="grid md:grid-cols-3 gap-6 mt-8">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-toxic-green flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-white mb-1">Vérification Rigoureuse</h3>
                      <p className="text-sm text-gray-400">Chaque information est testée et vérifiée avant publication</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Target className="w-6 h-6 text-toxic-green flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-white mb-1">Objectivité Totale</h3>
                      <p className="text-sm text-gray-400">Nos analyses restent indépendantes et impartiales</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <TrendingUp className="w-6 h-6 text-toxic-green flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-white mb-1">Mises à Jour Régulières</h3>
                      <p className="text-sm text-gray-400">Nos guides évoluent avec le marché</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-white">
              Statistiques et <span className="text-toxic-green">Tendances</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Chart
                title="Critères d'Évaluation des Plateformes"
                data={chart1Data}
                type="bar"
              />
              <Chart
                title="Répartition des Paris par Sport"
                data={chart2Data}
                type="pie"
              />
              <Chart
                title="Croissance du Marché (2021-2025)"
                data={chart3Data}
                type="line"
              />
            </div>
          </div>

          {/* Guide Links Section */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-white">
              Tous Nos <span className="text-toxic-green">Guides</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {guideLinks.map((guide, index) => (
                <article
                  key={guide.id}
                  itemScope
                  itemType="http://schema.org/Article"
                  className="bg-dark-card border-2 border-gray-800 rounded-xl p-6 hover:border-toxic-green transition-all transform hover:scale-105 group relative overflow-hidden"
                >
                  <Link
                    to={`/guides/${guide.slug}`}
                    data-discover="true"
                    itemProp="url"
                    className="block"
                  >
                    <div className="absolute top-4 right-4 w-12 h-12 border border-toxic-green opacity-20 rotate-45"></div>
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-2xl font-bold text-toxic-green opacity-50">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-toxic-green group-hover:translate-x-1 transition-all" />
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-white group-hover:text-toxic-green transition-colors" itemProp="headline">
                        {guide.title}
                      </h3>
                      <p className="text-gray-400 text-sm leading-relaxed" itemProp="description">
                        {guide.description}
                      </p>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Guides;
